import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import { apiClient } from './services/api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePermissions } from './hooks/usePermissions';
import ProjectWizard from './components/ProjectWizard/ProjectWizard';
import ProjectsList from './components/ProjectsList';
import ContentLayout from './components/ContentLayout/ContentLayout';
import CatalogueLayout from './components/CatalogueLayout/CatalogueLayout';
import ProjectWorkspace from './components/ProjectWorkspace';
import BrandManagement from './components/BrandManagement/BrandManagement';
import ChatInterface from './components/ChatInterface';
import ContentPreview from './components/ContentPreview';
import OfferExpired from './components/OfferExpired';
import LandingPagesList from './components/LandingPagesList';
import LandingPageCreator from './components/LandingPageCreator';
import LandingPageEditor from './components/LandingPage/LandingPageEditor';
import LandingPageLeads from './components/LandingPageLeads';
import QuizzesList from './components/QuizzesList';
import QuizCreator from './components/QuizCreator';
import ScriptsList from './components/Scripts/ScriptsList';
import ScriptCreator from './components/Scripts/ScriptCreator';
import SchedulingPage from './components/Scheduling/SchedulingPage';
import BrandPortal from './components/BrandPortal/BrandPortal';
import BuyerAccount from './components/BuyerPortal/BuyerAccount';
import ProjectSettings from './components/ProjectSettings/ProjectSettings';
import CRMWorkspace from './components/CRMWorkspace';
import UsersAdmin from './components/UsersAdmin';
import Layout from './components/Layout/Layout';
import LoginPage from './components/Auth/LoginPage';
import RegisterPage from './components/Auth/RegisterPage';
import ResetPassword from './components/Auth/ResetPassword';
import OAuthCallback from './components/Auth/OAuthCallback';
import VerifyEmailPage from './components/Auth/VerifyEmailPage';
import EmailVerificationModal from './components/Auth/EmailVerificationModal';
import UserProfile from './components/UserProfile/UserProfile';
import TeamsList from './components/Teams/TeamsList';
import TeamDetail from './components/Teams/TeamDetail';
import Dashboard from './components/Dashboard/Dashboard';
import OnboardingWizard from './components/Onboarding/OnboardingWizard';
import BillingPage from './components/Billing/BillingPage';
import AffiliateDashboard from './components/Affiliate/AffiliateDashboard';
import TrialExpiredModal from './components/Billing/TrialExpiredModal';
import TermsOfService from './components/Legal/TermsOfService';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import TosAcceptanceModal from './components/Legal/TosAcceptanceModal';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import FeedbackWidget from './components/FeedbackWidget/FeedbackWidget';
import CampaignsList from './components/Campaigns/CampaignsList';
import CampaignCreator from './components/Campaigns/CampaignCreator';
import CampaignDetail from './components/Campaigns/CampaignDetail';
import SEOModule from './components/SEO/SEOModule';
import LinkGraphPage from './components/SEO/LinkGraphPage';
import MultiChannelModule from './components/MultiChannel/MultiChannelModule';
import AdCopyModule from './components/AdCopy/AdCopyModule';
import AnalyticsModule from './components/Analytics/AnalyticsModule';
import LeadCaptureModule from './components/LeadCapture/LeadCaptureModule';
import FormIntakeModule from './components/FormIntake/FormIntakeModule';
import PublicFormFill from './components/FormIntake/PublicFormFill';
import BookingManager from './components/Booking/BookingManager';
import PublicBooking from './components/Booking/PublicBooking';
import PublicBookingList from './components/Booking/PublicBookingList';
import { isPublicCustomDomainHost } from './utils/publicHost';
import ManageBooking from './components/Booking/ManageBooking';
import CalendarConnectPage from './components/Booking/CalendarConnectPage';
import { CalendarConnectSuccess, CalendarConnectError } from './components/Booking/CalendarConnectResult';
import PublicAvailabilityPage from './components/Booking/PublicAvailabilityPage';
import ImagesModule from './components/Images/ImagesModule';
import VideoModule from './components/Video/VideoModule';
import ReviewsModule from './components/Reviews/ReviewsModule';
import AgentModule from './components/Agent/AgentModule';
import CoordinatorModule from './components/Coordinator/CoordinatorModule';
import MarketplaceModule from './components/Marketplace/MarketplaceModule';
import AutonomyModule from './components/Autonomy/AutonomyModule';
import DomainsModule from './components/Domains/DomainsModule';
import WordPressModule from './components/WordPress/WordPressModule';
import SocialAccountsModule from './components/SocialAccounts/SocialAccountsModule';
import OrganicGrowthModule from './components/OrganicGrowth/OrganicGrowthModule';
import IntegrationsModule from './components/Integrations/IntegrationsModule';
import ProductsModule from './components/Products/ProductsModule';
import ProductEditor from './components/Products/ProductEditor';
import { SiteBuilder, GenerationWizard, ImportedSiteEditor } from './components/SiteBuilder';
import ContentBrowser from './components/ContentBrowser/ContentBrowser';
import Homepage from './components/Homepage/Homepage';
import SupportPage from './components/Support/SupportPage';
import GenesisDrop from './components/Genesis/GenesisDrop';
import PredictionsModule from './components/Predictions/PredictionsModule';
import SendingEmailSettings from './components/Settings/SendingEmailSettings';
import SmsSendingSettings from './components/Settings/SmsSendingSettings';
import AIModelPreferences from './components/UserProfile/AIModelPreferences';
import SendingThrottleSettings from './components/Settings/SendingThrottleSettings';
import KnowledgePage from './components/KnowledgeBase/KnowledgePage';
import TicketsPage from './components/Support/TicketsPage';
import DataDeletionStatus from './components/DataDeletion/DataDeletionStatus';
import './App.css';

/**
 * Lazy import with automatic page reload on chunk-load failure.
 * After a deploy, browsers with a cached main bundle may request chunk hashes
 * that no longer exist. One reload is enough to pick up the new index.html
 * and fresh chunk references. A sessionStorage flag prevents infinite loops.
 */
function lazyWithReload<T extends React.ComponentType<any>>(
    importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
    return React.lazy(() =>
        importFn().catch((err) => {
            const key = 'chunk_reload_attempted';
            if (!sessionStorage.getItem(key)) {
                sessionStorage.setItem(key, '1');
                window.location.reload();
                return new Promise<{ default: T }>(() => {});
            }
            sessionStorage.removeItem(key);
            throw err;
        })
    );
}

// Lazy-loaded Optimization module
const OptimizationModule = lazyWithReload(() => import('./components/Optimization/OptimizationModule'));

// Lazy-loaded EDM module (TipTap is heavyweight — only load when needed)
const EDMModule = lazyWithReload(() => import('./components/EDM/EDMModule'));
const EDMTemplateEditor = lazyWithReload(() => import('./components/EDM/EDMTemplateEditor'));
const EDMCreateFromTemplate = lazyWithReload(() => import('./components/EDM/EDMCreateFromTemplate'));

// Computed once, at module scope — the hostname cannot change during a page's lifetime, so
// computing it per render risks an inconsistent route table between renders. True only when the
// current browser is on a customer's connected custom domain (not an AIME app host).
const PUBLIC_HOST = isPublicCustomDomainHost();

// Protected Route wrapper
interface ProtectedRouteProps {
    children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const location = useLocation();
    const [trialExpired, setTrialExpired] = useState(false);

    useEffect(() => {
        // Only check billing for non-admin authenticated users with TOS accepted
        if (!isAuthenticated || isLoading || !user || !user.tos_accepted_at) return;
        if (user.role === 'admin' || user.role === 'superadmin') return;

        apiClient.get('/billing/status').then(res => {
            const { status, plan } = res.data;
            if (
                status === 'trial_expired'
                || (status === 'trialing' && plan === 'trial' && res.data.trial_end && new Date(res.data.trial_end) < new Date())
            ) {
                setTrialExpired(true);
            }
        }).catch(() => {
            // Non-fatal — don't block access if billing check fails
        });
    }, [isAuthenticated, isLoading, user]);

    if (isLoading) {
        return (
            <div className="loading-container">
                <img src="/aim-logo.png" alt="AiMe" className="loading-logo" />
            </div>
        );
    }

    if (!isAuthenticated) {
        // Redirect to login, but save the attempted location
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Gate on T&C acceptance before anything else
    if (user && !user.tos_accepted_at) {
        return (
            <TosAcceptanceModal onAccepted={() => window.location.reload()} />
        );
    }

    // Gate on email verification — admins and OAuth users are pre-verified
    if (
        user &&
        !user.email_verified &&
        user.role !== 'admin' &&
        user.role !== 'superadmin'
    ) {
        return <EmailVerificationModal />;
    }

    // Gate on trial expiry — non-dismissable upgrade prompt
    if (trialExpired) {
        return <TrialExpiredModal />;
    }

    // Show onboarding wizard on first login (overlay, not a separate route)
    if (user && !user.onboarding_completed) {
        return (
            <>
                {children}
                <OnboardingWizard />
            </>
        );
    }

    return (
        <>
            {children}
            {/* Temporarily disabled while capturing documentation screenshots —
                NPS prompt was popping up on every hard refresh.
                Re-enable: uncomment the line below. */}
            {/* <FeedbackWidget /> */}
        </>
    );
};

// Module-guarded route — redirects to /dashboard if user lacks module access
interface ModuleRouteProps {
    moduleKey: string;
    children: React.ReactNode;
}

const ModuleRoute: React.FC<ModuleRouteProps> = ({ moduleKey, children }) => {
    const { canAccessModule } = usePermissions();
    if (!canAccessModule(moduleKey)) {
        return <Navigate to="/dashboard" replace />;
    }
    return <>{children}</>;
};

// Public route that redirects if already logged in
interface PublicRouteProps {
    children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="loading-container">
                <img src="/aim-logo.png" alt="AiMe" className="loading-logo" />
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
};

// Thin wrapper that plucks :landingPageId from the URL params
const LandingPageEditorRoute: React.FC = () => {
    const { landingPageId } = useParams<{ landingPageId: string }>();
    if (!landingPageId) return null;
    return <LandingPageEditor landingPageId={landingPageId} />;
};

// Root: show homepage for guests, redirect logged-in users to dashboard
const RootRoute: React.FC = () => {
    const { isAuthenticated, isLoading } = useAuth();
    if (isLoading) return (
        <div className="loading-container">
            <img src="/aim-logo.png" alt="AiMe" className="loading-logo" />
        </div>
    );
    if (isAuthenticated) return <Navigate to="/dashboard" replace />;
    return <Homepage />;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Root: homepage for guests, dashboard for authenticated users */}
            <Route path="/" element={<RootRoute />} />

            {/* Public auth routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/register"
                element={
                    <PublicRoute>
                        <RegisterPage />
                    </PublicRoute>
                }
            />
            {/* Password reset — public, handles both forgot + reset-with-token */}
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* OAuth SSO callback — public, processes token from URL then redirects */}
            <Route path="/oauth-callback" element={<OAuthCallback />} />

            {/* Email verification — public, handles token from verification email */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Legal pages — always public */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Public support page */}
            <Route path="/support" element={<SupportPage />} />

            {/* Standalone preview/portal routes (may be public) */}
            <Route path="/offer-expired" element={<OfferExpired />} />
            <Route path="/preview/:taskId" element={<ContentPreview />} />
            <Route path="/brand-portal/:brandGuideId" element={<BrandPortal />} />
            <Route path="/form-fill/:token" element={<PublicFormFill />} />
            <Route path="/book/:typeId" element={<PublicBooking />} />
            <Route path="/booking/manage/:token" element={<ManageBooking />} />
            {/* Public booking list + detail — customer custom-domain hosts only. Mutually
                exclusive with the authed /bookings route below (guarded !PUBLIC_HOST) so exactly
                one /bookings route exists in the tree at any time. See
                specs/completed/booking-custom-domain-routing.md "New Routes (App.tsx)". */}
            {PUBLIC_HOST && <Route path="/bookings" element={<PublicBookingList />} />}
            {PUBLIC_HOST && <Route path="/bookings/:typeId" element={<PublicBooking />} />}
            <Route path="/calendar-connect/success" element={<CalendarConnectSuccess />} />
            <Route path="/calendar-connect/error" element={<CalendarConnectError />} />
            <Route path="/calendar-connect/:token" element={<CalendarConnectPage />} />
            <Route path="/my-availability/:token" element={<PublicAvailabilityPage />} />
            <Route path="/buyer/account" element={<BuyerAccount />} />
            <Route path="/genesis" element={<GenesisDrop />} />
            <Route path="/data-deletion-status" element={<DataDeletionStatus />} />

            {/* Protected routes wrapped in Layout */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }
            >
                {/* Dashboard */}
                <Route path="/dashboard" element={<ErrorBoundary moduleName="Dashboard"><Dashboard /></ErrorBoundary>} />

                {/* Main app routes */}
                <Route path="/brand" element={<ModuleRoute moduleKey="brand"><ErrorBoundary moduleName="Brand"><BrandManagement /></ErrorBoundary></ModuleRoute>} />
                <Route path="/content" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Content"><ContentLayout /></ErrorBoundary></ModuleRoute>} />
                <Route path="/catalogue/articles" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Content Browser"><ContentBrowser view="articles" /></ErrorBoundary></ModuleRoute>} />
                <Route path="/catalogue/social" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Content Browser"><ContentBrowser view="social" /></ErrorBoundary></ModuleRoute>} />
                <Route path="/catalogue/seo" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Content Browser"><ContentBrowser view="seo" /></ErrorBoundary></ModuleRoute>} />
                <Route path="/catalogue/newsletters" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Content Browser"><ContentBrowser view="newsletters" /></ErrorBoundary></ModuleRoute>} />
                <Route path="/catalogue/calendars" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Content Browser"><ContentBrowser view="calendars" /></ErrorBoundary></ModuleRoute>} />
                <Route path="/catalogue" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Catalogue"><CatalogueLayout /></ErrorBoundary></ModuleRoute>} />
                <Route path="/site-builder" element={<ModuleRoute moduleKey="websites"><ErrorBoundary moduleName="Site Builder"><SiteBuilder /></ErrorBoundary></ModuleRoute>} />
                <Route path="/site-builder/new" element={<ModuleRoute moduleKey="websites"><ErrorBoundary moduleName="Site Builder"><GenerationWizard /></ErrorBoundary></ModuleRoute>} />
                <Route path="/site-builder/imported/:siteId" element={<ModuleRoute moduleKey="websites"><ErrorBoundary moduleName="Imported Site Editor"><ImportedSiteEditor /></ErrorBoundary></ModuleRoute>} />
                <Route path="/site-builder/:siteId" element={<ModuleRoute moduleKey="websites"><ErrorBoundary moduleName="Site Builder"><SiteBuilder /></ErrorBoundary></ModuleRoute>} />
                {/* CRM iframe is persistently mounted in Layout.tsx — this route just acts as a gate */}
                <Route path="/leads" element={<ModuleRoute moduleKey="leads_crm"><div /></ModuleRoute>} />
                <Route path="/admin" element={<ErrorBoundary moduleName="Admin"><UsersAdmin /></ErrorBoundary>} />
                <Route path="/admin/smtp" element={<ErrorBoundary moduleName="Email Sending"><SendingEmailSettings /></ErrorBoundary>} />
                <Route path="/admin/ai-models" element={<ErrorBoundary moduleName="AI Models"><AIModelPreferences /></ErrorBoundary>} />

                {/* Chat routes */}
                <Route path="/chat" element={<ErrorBoundary moduleName="Chat"><ChatInterface /></ErrorBoundary>} />
                <Route path="/chat/:conversationId" element={<ErrorBoundary moduleName="Chat"><ChatInterface /></ErrorBoundary>} />

                {/* Project routes */}
                <Route path="/projects" element={<ErrorBoundary moduleName="Projects"><ProjectsList /></ErrorBoundary>} />
                <Route path="/projects/new" element={<ErrorBoundary moduleName="New Project"><ProjectWizard /></ErrorBoundary>} />
                <Route path="/projects/:projectId" element={<ErrorBoundary moduleName="Project"><ProjectWorkspace /></ErrorBoundary>} />
                <Route path="/project/:projectId/settings" element={<ErrorBoundary moduleName="Project Settings"><ProjectSettings /></ErrorBoundary>} />

                {/* Landing pages */}
                <Route path="/landing-pages" element={<ModuleRoute moduleKey="landing_pages"><ErrorBoundary moduleName="Landing Pages"><LandingPagesList /></ErrorBoundary></ModuleRoute>} />
                <Route path="/landing-pages/create" element={<ModuleRoute moduleKey="landing_pages"><ErrorBoundary moduleName="Landing Page Creator"><LandingPageCreator /></ErrorBoundary></ModuleRoute>} />
                <Route path="/landing-pages/:landingPageId/edit" element={<ModuleRoute moduleKey="landing_pages"><ErrorBoundary moduleName="Landing Page Editor"><LandingPageEditorRoute /></ErrorBoundary></ModuleRoute>} />
                <Route path="/landing-pages/:landingPageId/leads" element={<ModuleRoute moduleKey="landing_pages"><ErrorBoundary moduleName="Landing Page Leads"><LandingPageLeads /></ErrorBoundary></ModuleRoute>} />

                {/* Quizzes */}
                <Route path="/quizzes" element={<ModuleRoute moduleKey="quizzes"><ErrorBoundary moduleName="Quizzes"><QuizzesList /></ErrorBoundary></ModuleRoute>} />
                <Route path="/quizzes/create" element={<ModuleRoute moduleKey="quizzes"><ErrorBoundary moduleName="Quiz Creator"><QuizCreator /></ErrorBoundary></ModuleRoute>} />

                {/* Scripts */}
                <Route path="/scripts" element={<ModuleRoute moduleKey="scripts"><ErrorBoundary moduleName="Scripts"><ScriptsList /></ErrorBoundary></ModuleRoute>} />
                <Route path="/scripts/create" element={<ModuleRoute moduleKey="scripts"><ErrorBoundary moduleName="Script Creator"><ScriptCreator /></ErrorBoundary></ModuleRoute>} />

                {/* Campaigns */}
                <Route path="/campaigns" element={<ModuleRoute moduleKey="campaigns"><ErrorBoundary moduleName="Campaigns"><CampaignsList /></ErrorBoundary></ModuleRoute>} />
                <Route path="/campaigns/create" element={<ModuleRoute moduleKey="campaigns"><ErrorBoundary moduleName="Campaign Creator"><CampaignCreator /></ErrorBoundary></ModuleRoute>} />
                <Route path="/campaigns/:campaignId" element={<ModuleRoute moduleKey="campaigns"><ErrorBoundary moduleName="Campaign"><CampaignDetail /></ErrorBoundary></ModuleRoute>} />

                {/* EDM (Email Marketing) */}
                <Route path="/edm" element={<ModuleRoute moduleKey="edm"><ErrorBoundary moduleName="Email Marketing"><React.Suspense fallback={<div style={{padding:'40px',textAlign:'center',color:'var(--text-secondary)'}}>Loading...</div>}><EDMModule /></React.Suspense></ErrorBoundary></ModuleRoute>} />
                <Route path="/edm/create" element={<ModuleRoute moduleKey="edm"><ErrorBoundary moduleName="EDM Template Editor"><React.Suspense fallback={<div style={{padding:'40px',textAlign:'center',color:'var(--text-secondary)'}}>Loading editor...</div>}><EDMTemplateEditor /></React.Suspense></ErrorBoundary></ModuleRoute>} />
                <Route path="/edm/create-sequence" element={<ModuleRoute moduleKey="edm"><ErrorBoundary moduleName="EDM Create Sequence"><React.Suspense fallback={<div style={{padding:'40px',textAlign:'center',color:'var(--text-secondary)'}}>Loading...</div>}><EDMCreateFromTemplate /></React.Suspense></ErrorBoundary></ModuleRoute>} />
                <Route path="/edm/:templateId" element={<ModuleRoute moduleKey="edm"><ErrorBoundary moduleName="EDM Template Editor"><React.Suspense fallback={<div style={{padding:'40px',textAlign:'center',color:'var(--text-secondary)'}}>Loading editor...</div>}><EDMTemplateEditor /></React.Suspense></ErrorBoundary></ModuleRoute>} />

                {/* SEO */}
                <Route path="/seo" element={<ModuleRoute moduleKey="seo"><ErrorBoundary moduleName="SEO"><SEOModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/seo/link-graph" element={<ModuleRoute moduleKey="seo"><ErrorBoundary moduleName="Link Graph"><LinkGraphPage /></ErrorBoundary></ModuleRoute>} />

                {/* Multi-Channel */}
                <Route path="/multichannel" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Multi-Channel"><MultiChannelModule /></ErrorBoundary></ModuleRoute>} />

                {/* Ad Copy */}
                <Route path="/ad-copy" element={<ModuleRoute moduleKey="campaigns"><ErrorBoundary moduleName="Ad Copy"><AdCopyModule /></ErrorBoundary></ModuleRoute>} />

                {/* Analytics */}
                <Route path="/analytics" element={<ModuleRoute moduleKey="analytics"><ErrorBoundary moduleName="Analytics"><AnalyticsModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/predictions" element={<ModuleRoute moduleKey="analytics"><ErrorBoundary moduleName="Predictions"><PredictionsModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/optimization" element={<ModuleRoute moduleKey="optimization"><ErrorBoundary moduleName="Optimization"><React.Suspense fallback={<div style={{padding:'40px',textAlign:'center',color:'var(--text-secondary)'}}>Loading...</div>}><OptimizationModule /></React.Suspense></ErrorBoundary></ModuleRoute>} />
                <Route path="/lead-capture" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Lead Capture"><LeadCaptureModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/form-intake" element={<ModuleRoute moduleKey="form_intake"><ErrorBoundary moduleName="CRM Form Builder"><FormIntakeModule /></ErrorBoundary></ModuleRoute>} />
                {!PUBLIC_HOST && (
                    <Route path="/bookings" element={<ModuleRoute moduleKey="form_intake"><ErrorBoundary moduleName="Booking Management"><BookingManager /></ErrorBoundary></ModuleRoute>} />
                )}
                <Route path="/images" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Images"><ImagesModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/video" element={<ModuleRoute moduleKey="video"><ErrorBoundary moduleName="Video"><VideoModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/reviews" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Reviews"><ReviewsModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/agent" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="AI Agent"><AgentModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/coordinator" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Coordinator"><CoordinatorModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/marketplace" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Marketplace"><MarketplaceModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/autonomy" element={<ModuleRoute moduleKey="content"><ErrorBoundary moduleName="Autonomy"><AutonomyModule /></ErrorBoundary></ModuleRoute>} />

                {/* Domains */}
                <Route path="/domains" element={<ModuleRoute moduleKey="domains"><ErrorBoundary moduleName="Domains"><DomainsModule /></ErrorBoundary></ModuleRoute>} />

                {/* WordPress */}
                <Route path="/wordpress" element={<ModuleRoute moduleKey="campaigns"><ErrorBoundary moduleName="WordPress"><WordPressModule /></ErrorBoundary></ModuleRoute>} />

                {/* Social Accounts */}
                <Route path="/social-accounts" element={<ModuleRoute moduleKey="campaigns"><ErrorBoundary moduleName="Social Accounts"><SocialAccountsModule /></ErrorBoundary></ModuleRoute>} />

                {/* Organic Growth */}
                <Route path="/organic-growth" element={<ModuleRoute moduleKey="campaigns"><ErrorBoundary moduleName="Organic Growth"><OrganicGrowthModule /></ErrorBoundary></ModuleRoute>} />

                {/* Integrations */}
                <Route path="/integrations" element={<ModuleRoute moduleKey="integrations"><ErrorBoundary moduleName="Integrations"><IntegrationsModule /></ErrorBoundary></ModuleRoute>} />

                {/* Products */}
                <Route path="/products" element={<ModuleRoute moduleKey="products"><ErrorBoundary moduleName="Products"><ProductsModule /></ErrorBoundary></ModuleRoute>} />
                <Route path="/products/new" element={<ModuleRoute moduleKey="products"><ErrorBoundary moduleName="Product Editor"><ProductEditor /></ErrorBoundary></ModuleRoute>} />
                <Route path="/products/:productId" element={<ModuleRoute moduleKey="products"><ErrorBoundary moduleName="Product Editor"><ProductEditor /></ErrorBoundary></ModuleRoute>} />

                {/* Scheduling */}
                <Route path="/scheduling/:projectId" element={<ModuleRoute moduleKey="scheduling"><ErrorBoundary moduleName="Scheduling"><SchedulingPage /></ErrorBoundary></ModuleRoute>} />

                {/* User profile */}
                <Route path="/profile" element={<ErrorBoundary moduleName="Profile"><UserProfile /></ErrorBoundary>} />
                <Route path="/settings/sending-email" element={<ErrorBoundary moduleName="Sending Email Settings"><SendingEmailSettings /></ErrorBoundary>} />
                <Route path="/settings/sending-throttle" element={<ErrorBoundary moduleName="Sending Throttle Settings"><SendingThrottleSettings /></ErrorBoundary>} />
                <Route path="/settings/sms" element={<ModuleRoute moduleKey="sms"><ErrorBoundary moduleName="SMS Sending"><SmsSendingSettings /></ErrorBoundary></ModuleRoute>} />

                {/* Knowledge Base */}
                <Route path="/knowledge-base" element={<ErrorBoundary moduleName="Knowledge Base"><KnowledgePage /></ErrorBoundary>} />

                {/* Support Tickets */}
                <Route path="/tickets" element={<ErrorBoundary moduleName="Support Tickets"><TicketsPage /></ErrorBoundary>} />

                {/* Billing */}
                <Route path="/billing" element={<ErrorBoundary moduleName="Billing"><BillingPage /></ErrorBoundary>} />

                {/* Affiliate Programme */}
                <Route path="/affiliate" element={<ErrorBoundary moduleName="Affiliate"><AffiliateDashboard /></ErrorBoundary>} />

                {/* Onboarding (standalone route — wizard also shows as overlay on first login) */}
                <Route path="/onboarding" element={<ErrorBoundary moduleName="Onboarding"><OnboardingWizard /></ErrorBoundary>} />

                {/* Teams */}
                <Route path="/teams" element={<ErrorBoundary moduleName="Teams"><TeamsList /></ErrorBoundary>} />
                <Route path="/teams/:teamId" element={<ErrorBoundary moduleName="Team"><TeamDetail /></ErrorBoundary>} />
            </Route>
        </Routes>
    );
}

function App() {
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        if (ref && ref.length >= 4) {
            const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
            localStorage.setItem('aime_ref', JSON.stringify({ code: ref.toUpperCase(), expiry }));
        }
    }, []);

    return (
        <Router>
            <AuthProvider>
                <div className="App">
                    <AppRoutes />
                </div>
            </AuthProvider>
        </Router>
    );
}

export default App;
